import {useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {dataClient} from '../data/dataClient';
import {useWorkspace} from '../hooks/useWorkspace';
import {parseBusinessDescription} from '../services/introParser.js';

const {FiArrowRight, FiCheck, FiSparkles, FiRefreshCw} = FiIcons;

export default function Introduction() {
  const {business, brandProfile} = useWorkspace();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: business?.name || '',
    industry: business?.industry || '',
    audience: business?.audience || '',
    goals: business?.goals || '',
    voice: brandProfile?.voice || '',
    avoid: brandProfile?.avoid || ''
  });
  const [freeText, setFreeText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parseNotice, setParseNotice] = useState(null);
  const [saved, setSaved] = useState(false);

  function field(name) {
    return {
      value: form[name] || '',
      onChange: (event) => setForm({...form, [name]: event.target.value})
    };
  }

  async function handleAutoFill() {
    if (!freeText.trim() || parsing) return;
    setParsing(true);
    setParseNotice('Extracting business context…');

    try {
      const extracted = await parseBusinessDescription(freeText);
      setForm((prev) => ({
        ...prev,
        name: extracted.name || prev.name,
        industry: extracted.industry || prev.industry,
        audience: extracted.audience || prev.audience,
        goals: extracted.goals || prev.goals,
        voice: extracted.voice || prev.voice,
        avoid: extracted.avoid || prev.avoid
      }));
      setParseNotice('Fields auto-filled! You can review and adjust each below.');
    } catch (err) {
      setParseNotice(`Extraction failed: ${err.message}`);
    } finally {
      setParsing(false);
    }
  }

  async function finish() {
    await dataClient.setBusiness(
      {name: form.name, industry: form.industry, audience: form.audience, goals: form.goals},
      {voice: form.voice, avoid: form.avoid}
    );
    setSaved(true);
  }

  return (
    <div className="page narrow">
      <header className="page-header">
        <span className="eyebrow">Introduction</span>
        <h1>Teach your teams how your business thinks.</h1>
        <p>A shared context keeps every specialist aligned without repeating the brief.</p>
      </header>

      {/* Free-text Quick Parser */}
      <section className="panel" style={{marginBottom: '20px', background: 'var(--color-surfaceAlt)'}}>
        <div className="section-heading">
          <div className="inline" style={{gap: '8px'}}>
            <SafeIcon icon={FiSparkles} style={{color: 'var(--color-gold)'}} />
            <h3>Quick Auto-Fill from Description</h3>
          </div>
          <small className="muted">Deterministic parser + AI</small>
        </div>

        <p style={{fontSize: 'var(--type-xs)', color: 'var(--color-muted)', margin: '0 0 10px'}}>
          Paste a summary of your business, audience, and voice. The parser extracts structured fields into the wizard below.
        </p>

        <textarea
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          placeholder="e.g. “We are Northstar Studio, an organic home goods brand for design-conscious urban homeowners. Our goal is growing subscription revenue. We sound warm, precise, and optimistic, and we avoid aggressive hype or artificial discounts.”"
          style={{minHeight: '80px', marginBottom: '10px'}}
        />

        <div className="inline spread">
          <button
            type="button"
            className="button secondary compact"
            onClick={handleAutoFill}
            disabled={parsing || !freeText.trim()}
          >
            <SafeIcon icon={parsing ? FiRefreshCw : FiSparkles} />
            {parsing ? 'Parsing…' : 'Parse and auto-fill'}
          </button>
          {parseNotice && <span style={{fontSize: '11px', color: '#15803d'}}>{parseNotice}</span>}
        </div>
      </section>

      {/* Structured Wizard */}
      <section className="wizard panel">
        <div className="step-row">
          {['Business', 'Audience', 'Voice'].map((name, index) => (
            <span
              className={step >= index + 1 ? 'active' : ''}
              key={name}
              onClick={() => setStep(index + 1)}
              style={{cursor: 'pointer'}}
            >
              {index + 1}. {name}
            </span>
          ))}
        </div>

        {step === 1 && (
          <div className="form-grid">
            <label>
              Business name
              <input {...field('name')} placeholder="e.g. Northstar Studio" />
            </label>
            <label>
              Industry
              <input {...field('industry')} placeholder="e.g. Sustainable home goods" />
            </label>
          </div>
        )}

        {step === 2 && (
          <div className="form-grid">
            <label className="full">
              Primary audience
              <textarea {...field('audience')} placeholder="Who are your core customers or clients?" />
            </label>
            <label className="full">
              Current goal
              <textarea {...field('goals')} placeholder="What strategic priorities should your AI teams focus on?" />
            </label>
          </div>
        )}

        {step === 3 && (
          <div className="form-grid">
            <label className="full">
              Brand voice
              <textarea {...field('voice')} placeholder="e.g. Warm, precise, optimistic, understated confidence" />
            </label>
            <label className="full">
              Language to avoid
              <textarea {...field('avoid')} placeholder="e.g. Artificial urgency, aggressive discounts, corporate jargon" />
            </label>
          </div>
        )}

        <div className="wizard-actions">
          {step > 1 && (
            <button className="button ghost" onClick={() => setStep(step - 1)}>
              Back
            </button>
          )}
          {step < 3 ? (
            <button className="button primary" onClick={() => setStep(step + 1)}>
              Continue <SafeIcon icon={FiArrowRight} />
            </button>
          ) : (
            <button className="button primary" onClick={finish}>
              <SafeIcon icon={FiCheck} /> Save context
            </button>
          )}
        </div>

        {saved && (
          <div className="success-note">
            Business details and brand profile saved to workspace context.
          </div>
        )}
      </section>
    </div>
  );
}
